/**
 * The diff card — PLAN §4.4's marquee surface.
 *
 * A staged changeset, rendered as the changes it would make and the numbers they
 * would move, with accept and reject. Three things about it are load-bearing:
 *
 * 1. **The preview is computed locally**, by applying the changeset to a copy of
 *    the workspace and running the same engine the vitals rail runs. "IBU 42 →
 *    35.2" is therefore a promise the editor keeps the moment you accept, not a
 *    figure from a server that has since fallen behind.
 * 2. **Accept goes through the ordinary store dispatch.** The commands the
 *    assistant wrote take the identical path a keystroke takes, so sync, history
 *    and undo need no special case — and one ⌘Z undoes the whole proposal,
 *    because the store records a batch as one history entry.
 * 3. **Stale is a state, not a failure.** If the recipe moved since the proposal
 *    was staged the card says so and offers a re-check rather than applying a
 *    plan drawn against a workspace that no longer exists.
 *
 * Per-change toggles are offered, and quietly withdrawn when the changes turn
 * out to depend on each other — a changeset is an ordered log, so deselecting
 * "add Citra" while keeping "change Citra to 25 g" is not a smaller edit, it is
 * an invalid one. The preview detects that by trying it.
 */

import type { Command } from '@beerwright/commands';
import { AlertTriangleIcon, CheckIcon, RefreshCwIcon, XIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAssistant } from '@/ai/assistant-context';
import type { ChangesetPreview, VitalDelta } from '@/ai/preview';
import { isStale, previewChangeset } from '@/ai/preview';
import type { AiProposal } from '@/ai/protocol';
import { AssistantOrb } from '@/components/assistant-orb';
import { useToast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { colorUnitLabel, formatColor, formatGravity, formatNumber } from '@/lib/format';
import { cmd } from '@/lib/keys';
import { userContent } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import { useUnits, useWorkspaceSelector, useWorkspaceStore } from '@/store/workspace-context';

export interface ProposalCardProps {
  readonly proposal: AiProposal;
  /** Called with the created recipe id when accepting a proposal that creates one. */
  readonly onCreated?: (recipeId: string) => void;
}

export function ProposalCard({ proposal, onCreated }: ProposalCardProps) {
  const store = useWorkspaceStore();
  const units = useUnits();
  const { toast } = useToast();
  const { outcomeOf, resolve } = useAssistant();
  const outcome = outcomeOf(proposal.changeset.changesetId);

  const [selected, setSelected] = useState<ReadonlySet<number>>(
    () => new Set(proposal.changeset.changes.map((_, index) => index)),
  );
  /**
   * The `updatedAt` this card is judging staleness against.
   *
   * Starts at what the assistant saw; "re-check" moves it to what the workspace
   * says now, which both clears the warning and re-runs the preview against the
   * current document — the two halves of what re-checking means.
   */
  const [checkedAgainst, setCheckedAgainst] = useState(proposal.recipeUpdatedAt);
  /**
   * The preview as it stood when the user decided.
   *
   * Frozen, because a resolved card is a record of what happened. Left live, it
   * would recompute against a workspace that already contains the change and
   * show a second application of it — a proposal that reads "IBU 135 → 138"
   * *after* it took IBU to 135. The rail behind the card is the live number.
   */
  const [decided, setDecided] = useState<ChangesetPreview | null>(null);

  const state = useWorkspaceSelector((current) => current);
  const targetRecipeId = proposal.recipeId;

  const stale = useMemo(
    () => isStale(state, targetRecipeId, checkedAgainst),
    [state, targetRecipeId, checkedAgainst],
  );

  // The preview follows `state`, so it re-runs on any workspace change —
  // including the one that made this card stale.
  const live = useMemo(
    () => previewChangeset(state, proposal.changeset, selected, targetRecipeId),
    [state, proposal.changeset, selected, targetRecipeId],
  );
  const preview = decided ?? live;

  const allSelected = selected.size === proposal.changeset.changes.length;

  function toggle(index: number): void {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  function accept(): void {
    const commands = proposal.changeset.changes
      .filter((_, index) => selected.has(index))
      .map((change) => change.command as Command);
    if (commands.length === 0) return;

    // One batch, one history entry: ⌘Z reverts the whole proposal.
    const result = store.dispatchBatch(commands, `Assistant: ${proposal.changeset.summary}`);
    if (!result.ok) {
      toast({
        variant: 'error',
        title: 'We could not apply that proposal',
        description: result.error.message,
      });
      return;
    }
    setDecided(live);
    resolve(proposal.changeset.changesetId, 'accepted');
    toast({
      title: 'Accepted',
      description: `${proposal.changeset.summary}. Press ${cmd('Z')} to undo the whole proposal.`,
    });
    // Navigate to the id the dispatched `recipe.create` actually carried — not
    // `proposal.recipeId`, which is the model's top-level tool argument and can
    // disagree with the payload id (navigating to a recipe that was never
    // created). Deriving it from the accepted commands also covers a create
    // that was deselected: no create dispatched, no navigation.
    const created = commands.find(
      (command): command is Extract<Command, { type: 'recipe.create' }> =>
        command.type === 'recipe.create',
    );
    if (created !== undefined) onCreated?.(created.payload.recipeId);
  }

  return (
    <section
      data-slot="proposal-card"
      aria-label={`Proposal: ${proposal.changeset.summary}`}
      className={cn(
        'flex flex-col gap-3 rounded-lg border bg-card px-3 py-3',
        outcome === 'accepted' && 'border-success/40 bg-success/5',
        outcome === 'rejected' && 'border-border opacity-60',
        outcome === null && stale && 'border-warning/50',
        outcome === null && !stale && 'border-primary/40',
      )}
    >
      <header className="flex items-start gap-2">
        <AssistantOrb className="mt-0.5 size-4 shrink-0" />
        <div className="min-w-0 flex-1">
          {/* Both name the recipe and its ingredients back to the brewer. */}
          <p {...userContent} className="text-sm font-medium">
            {proposal.changeset.summary}
          </p>
          {proposal.changeset.rationale !== '' && (
            <p {...userContent} className="mt-0.5 text-xs leading-snug text-muted-foreground">
              {proposal.changeset.rationale}
            </p>
          )}
        </div>
      </header>

      {stale && outcome === null && (
        <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 px-2 py-1.5">
          <AlertTriangleIcon className="mt-0.5 size-3.5 shrink-0 text-warning" aria-hidden="true" />
          <div className="min-w-0 flex-1 text-[11px] leading-snug text-muted-foreground">
            The recipe changed after this was proposed, so the preview may not match what the
            assistant saw.
          </div>
          <Button
            variant="ghost"
            size="xs"
            className="shrink-0 text-muted-foreground"
            onClick={() =>
              setCheckedAgainst(
                targetRecipeId === null ? null : (state.recipes[targetRecipeId]?.updatedAt ?? null),
              )
            }
          >
            <RefreshCwIcon />
            Re-check
          </Button>
        </div>
      )}

      <ul className="flex flex-col gap-1">
        {proposal.changeset.changes.map((change, index) => (
          <li key={change.command.commandId}>
            <label
              className={cn(
                'flex items-start gap-2 rounded-md px-1.5 py-1 text-xs leading-snug',
                outcome === null && 'cursor-pointer transition-colors hover:bg-accent/50',
                !selected.has(index) && 'text-muted-foreground line-through decoration-1',
              )}
            >
              <input
                type="checkbox"
                checked={selected.has(index)}
                disabled={outcome !== null}
                onChange={() => toggle(index)}
                className="mt-0.5 size-3.5 shrink-0 accent-primary"
                aria-label={change.summary}
              />
              <span {...userContent} className="min-w-0 flex-1">
                {change.summary}
              </span>
            </label>
          </li>
        ))}
      </ul>

      {preview.ok ? (
        <VitalsPreview deltas={preview.deltas} units={units} />
      ) : (
        <p className="rounded-md bg-muted/60 px-2 py-1.5 text-[11px] text-muted-foreground">
          {preview.reason}
        </p>
      )}

      {outcome === null ? (
        <footer className="flex items-center gap-2">
          <Button size="sm" onClick={accept} disabled={!preview.ok || selected.size === 0}>
            <CheckIcon />
            {allSelected ? 'Accept all' : `Accept ${selected.size}`}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground"
            onClick={() => {
              setDecided(live);
              resolve(proposal.changeset.changesetId, 'rejected');
            }}
          >
            <XIcon />
            Reject
          </Button>
        </footer>
      ) : (
        <footer
          aria-live="polite"
          className={cn(
            'flex h-8 items-center gap-1.5 text-sm font-medium',
            outcome === 'accepted' ? 'text-success' : 'text-muted-foreground',
          )}
        >
          {outcome === 'accepted' ? (
            <CheckIcon className="size-4" aria-hidden="true" />
          ) : (
            <XIcon className="size-4" aria-hidden="true" />
          )}
          {outcome === 'accepted' ? 'Accepted' : 'Rejected'}
        </footer>
      )}
    </section>
  );
}

/** The current → proposed row for each vital, coloured by where it lands. */
function VitalsPreview({
  deltas,
  units,
}: {
  readonly deltas: readonly VitalDelta[];
  readonly units: ReturnType<typeof useUnits>;
}) {
  const format = (delta: VitalDelta, value: number): string => {
    switch (delta.key) {
      case 'og':
      case 'fg':
        return formatGravity(value, units);
      case 'ibu':
        return formatNumber(value, 0);
      case 'srm':
        return formatColor(value, units);
      case 'abv':
        return `${formatNumber(value, 1)} %`;
    }
  };

  return (
    <dl
      data-slot="vitals-preview"
      className="grid grid-cols-5 gap-px overflow-hidden rounded-md border border-border bg-border"
    >
      {deltas.map((delta) => {
        const changed = delta.current === null || Math.abs(delta.proposed - delta.current) > 1e-9;
        return (
          <div key={delta.key} className="flex flex-col gap-0.5 bg-card px-1.5 py-1.5 text-center">
            <dt className="text-[9px] font-medium tracking-wide text-muted-foreground uppercase">
              {/* Colour is the one vital whose heading is its unit. */}
              {delta.key === 'srm' ? colorUnitLabel(units) : delta.label}
            </dt>
            <dd className="flex flex-col items-center leading-tight">
              {delta.current !== null && changed && (
                <span className="font-mono text-[10px] text-muted-foreground line-through decoration-1">
                  {format(delta, delta.current)}
                </span>
              )}
              <span
                className={cn(
                  'font-mono text-[11px] tabular-nums',
                  delta.entersRange && 'text-success',
                  delta.leavesRange && 'text-warning',
                  !delta.entersRange &&
                    !delta.leavesRange &&
                    (delta.status === 'below' || delta.status === 'above') &&
                    'text-warning',
                )}
                title={
                  delta.range === null
                    ? 'No style selected'
                    : `Style range ${format(delta, delta.range.min)}–${format(delta, delta.range.max)}`
                }
              >
                {format(delta, delta.proposed)}
              </span>
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
